import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { Patient, PersonalDetails, Encounter } from '../types';
import { SearchIcon, UserPlusIcon, FileTextIcon } from 'lucide-react';
export const ReceptionDashboard: React.FC = () => {
  const {
    patients,
    addPatient,
    updatePatient,
    generateFileNo,
    addEncounter,
    addNotification
  } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const initialFormState: PersonalDetails = {
    fileNo: '',
    date: new Date().toISOString().split('T')[0],
    fullNames: '',
    sex: '',
    age: '',
    maritalStatus: '',
    homeAddress: '',
    contact: '',
    occupation: '',
    nextOfKin: {
      name: '',
      contact: '',
      relationship: ''
    },
    allergies: '',
    hivStatus: ''
  };
  const [formData, setFormData] = useState<PersonalDetails>(initialFormState);
  const filteredPatients = patients.filter(p => p.personalDetails.fullNames.toLowerCase().includes(searchTerm.toLowerCase()) || p.personalDetails.fileNo.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleOpenForm = (patient?: Patient) => {
    if (patient) {
      setEditingPatient(patient);
      setFormData(patient.personalDetails);
    } else {
      setEditingPatient(null);
      setFormData({
        ...initialFormState,
        fileNo: generateFileNo()
      });
    }
    setShowForm(true);
  };
  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    let patientId = editingPatient?.id;
    if (editingPatient) {
      updatePatient({
        ...editingPatient,
        personalDetails: formData
      });
    } else {
      patientId = Date.now().toString();
      const newPatient: Patient = {
        id: patientId,
        personalDetails: formData,
        encounters: []
      };
      addPatient(newPatient);
    }
    // Create a new encounter and send to nursing
    const newEncounter: Encounter = {
      id: Date.now().toString(),
      patientId: patientId!,
      date: new Date().toISOString(),
      status: 'With Nursing',
      vitals: [],
      clinicalNotes: {
        presentingComplaint: '',
        historyOfPresentingComplaint: '',
        pastMedicalHistory: {
          diabetes: false,
          epilepsy: false,
          tuberculosis: false,
          hypertension: false,
          asthma: false,
          sickleCell: false,
          anemia: false,
          rvd: false
        },
        drugHistory: '',
        knownAllergies: formData.allergies,
        familyMedicalHistory: {
          diabetes: false,
          epilepsy: false,
          tuberculosis: false,
          hypertension: false,
          asthma: false,
          sickleCell: false,
          anemia: false
        },
        obGynHistory: '',
        socialHistory: {
          alcohol: '',
          smoking: '',
          occupation: formData.occupation,
          mentalStatus: ''
        },
        reviewOfSystems: {
          rs: '',
          cvs: '',
          cns: '',
          git: '',
          gut: '',
          mss: '',
          is: ''
        },
        onExamination: {
          generalCondition: '',
          fever: '',
          pjc: '',
          s1s2: '',
          rs: '',
          pa: ''
        },
        summaryOfFindings: [],
        workingDiagnosis: []
      },
      investigations: {
        ordered: {
          fbc: false,
          bloodGroup: false,
          cd4Count: false,
          urea: false,
          creatinine: false,
          fbs: false,
          rbs: false,
          gtt: false,
          postPrandialSugar: false,
          rpr: false,
          hiv: false,
          syphilis: false,
          genitalSwab: false,
          gonorrheaAg: false,
          urinalysis: false,
          mpsRdt: false,
          hbsAg: false,
          hbcAg: false,
          hbeAg: false,
          hcv: false,
          hav: false,
          viralLoad: false,
          gravindex: false,
          hvs: false,
          creatineUa: false,
          lft: false,
          papSmear: false,
          aafb: false,
          urineMicroscopy: false,
          urethralSwab: false,
          vbg: false,
          hepatitisProfile: false,
          rbsFbs: false,
          abg: false,
          stoolMicroscopy: false,
          doa6: false,
          cxr: false,
          scanUltrasound: false,
          other: ''
        },
        labResults: {},
        scanType: '',
        radiologyImages: [],
        radiologyReport: '',
        radiographerName: ''
      },
      prescriptions: [],
      followUpDates: []
    };
    addEncounter(patientId!, newEncounter);
    addNotification({
      targetRole: 'Nursing',
      message: `New patient file sent: ${formData.fullNames} (${formData.fileNo})`,
      patientId: patientId!
    });
    setShowForm(false);
  };
  if (showForm) {
    return <div className="bg-white shadow rounded-lg border border-gray-100 p-6 max-w-4xl mx-auto" data-id="element-987">
        <div className="flex justify-between items-center mb-6 border-b pb-4" data-id="element-988">
          <h2 className="text-xl font-bold text-gray-800" data-id="element-989">
            {editingPatient ? 'Update Patient Details' : 'Register New Patient'}
          </h2>
          <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700" data-id="element-990">
            Cancel
          </button>
        </div>

        <form onSubmit={handleSave} className="space-y-6" data-id="element-991">
          <div className="bg-red-50 text-red-700 p-3 rounded text-sm font-bold text-center border border-red-200" data-id="element-992">
            CONFIDENTIAL
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-993">
            <div data-id="element-994">
              <label className="block text-sm font-medium text-gray-700" data-id="element-995">
                File No.
              </label>
              <input type="text" readOnly value={formData.fileNo} className="mt-1 block w-full bg-gray-50 border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm" data-id="element-996" />
            </div>
            <div data-id="element-997">
              <label className="block text-sm font-medium text-gray-700" data-id="element-998">
                Date
              </label>
              <input type="date" required value={formData.date} onChange={e => setFormData({
              ...formData,
              date: e.target.value
            })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-999" />
            </div>

            <div className="md:col-span-2" data-id="element-1000">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1001">
                Full Names
              </label>
              <input type="text" required value={formData.fullNames} onChange={e => setFormData({
              ...formData,
              fullNames: e.target.value
            })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" placeholder="e.g. Mwamba Chilufya" data-id="element-1002" />
            </div>

            <div data-id="element-1003">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1004">
                Sex
              </label>
              <select required value={formData.sex} onChange={e => setFormData({
              ...formData,
              sex: e.target.value as any
            })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-1005">
                <option value="" data-id="element-1006">Select...</option>
                <option value="Male" data-id="element-1007">Male</option>
                <option value="Female" data-id="element-1008">Female</option>
              </select>
            </div>

            <div data-id="element-1009">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1010">
                Age
              </label>
              <input type="number" required value={formData.age} onChange={e => setFormData({
              ...formData,
              age: e.target.value
            })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-1011" />
            </div>

            <div data-id="element-1012">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1013">
                Marital Status
              </label>
              <select required value={formData.maritalStatus} onChange={e => setFormData({
              ...formData,
              maritalStatus: e.target.value as any
            })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-1014">
                <option value="" data-id="element-1015">Select...</option>
                <option value="Married" data-id="element-1016">Married</option>
                <option value="Single" data-id="element-1017">Single</option>
              </select>
            </div>

            <div data-id="element-1018">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1019">
                Occupation
              </label>
              <input type="text" value={formData.occupation} onChange={e => setFormData({
              ...formData,
              occupation: e.target.value
            })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-1020" />
            </div>

            <div className="md:col-span-2" data-id="element-1021">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1022">
                Home Address
              </label>
              <input type="text" required value={formData.homeAddress} onChange={e => setFormData({
              ...formData,
              homeAddress: e.target.value
            })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-1023" />
            </div>

            <div data-id="element-1024">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1025">
                Contact
              </label>
              <input type="text" required value={formData.contact} onChange={e => setFormData({
              ...formData,
              contact: e.target.value
            })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-1026" />
            </div>

            <div data-id="element-1027">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1028">
                Allergies
              </label>
              <input type="text" value={formData.allergies} onChange={e => setFormData({
              ...formData,
              allergies: e.target.value
            })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-1029" />
            </div>
          </div>

          <div className="border-t pt-4 mt-6" data-id="element-1030">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1031">
              Next of Kin
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1032">
              <div data-id="element-1033">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1034">
                  Name
                </label>
                <input type="text" required value={formData.nextOfKin.name} onChange={e => setFormData({
                ...formData,
                nextOfKin: {
                  ...formData.nextOfKin,
                  name: e.target.value
                }
              })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-1035" />
              </div>
              <div data-id="element-1036">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1037">
                  Contact
                </label>
                <input type="text" required value={formData.nextOfKin.contact} onChange={e => setFormData({
                ...formData,
                nextOfKin: {
                  ...formData.nextOfKin,
                  contact: e.target.value
                }
              })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-1038" />
              </div>
              <div data-id="element-1039">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1040">
                  Relationship
                </label>
                <input type="text" required value={formData.nextOfKin.relationship} onChange={e => setFormData({
                ...formData,
                nextOfKin: {
                  ...formData.nextOfKin,
                  relationship: e.target.value
                }
              })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" data-id="element-1041" />
              </div>
            </div>
          </div>

          <div className="border-t pt-4 mt-6" data-id="element-1042">
            <label className="block text-sm font-medium text-gray-700" data-id="element-1043">
              HIV Status
            </label>
            <div className="mt-2 space-x-4 flex" data-id="element-1044">
              {['Positive', 'Negative', 'Unknown'].map(status => <label key={status} className="inline-flex items-center" data-id="element-1045">
                  <input type="radio" required name="hivStatus" value={status} checked={formData.hivStatus === status} onChange={e => setFormData({
                ...formData,
                hivStatus: e.target.value as any
              })} className="form-radio h-4 w-4 text-teal-600 border-gray-300 focus:ring-teal-500" data-id="element-1046" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-1047">{status}</span>
                </label>)}
            </div>
          </div>

          {editingPatient && editingPatient.encounters.length > 0 && <div className="border-t pt-4 mt-6" data-id="element-1048">
              <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1049">
                Visit History
              </h3>
              <div className="bg-gray-50 rounded-md p-4 border border-gray-200 max-h-40 overflow-y-auto" data-id="element-1050">
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1051">
                  {editingPatient.encounters.slice().reverse().map((enc, idx) => <li key={enc.id} className="flex justify-between items-center border-b border-gray-200 pb-2 last:border-0 last:pb-0" data-id="element-1052">
                        <span data-id="element-1053">
                          {new Date(enc.date).toLocaleDateString()} -{' '}
                          {new Date(enc.date).toLocaleTimeString()}
                        </span>
                        <span className="px-2 py-1 bg-gray-200 rounded text-xs font-medium" data-id="element-1054">
                          {enc.status}
                        </span>
                      </li>)}
                </ul>
              </div>
            </div>}

          <div className="pt-5 border-t mt-6" data-id="element-1055">
            <div className="flex justify-end" data-id="element-1056">
              <button type="button" onClick={() => setShowForm(false)} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" data-id="element-1057">
                Cancel
              </button>
              <button type="submit" className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none" data-id="element-1058">
                Save & Send to Nursing
              </button>
            </div>
          </div>
        </form>
      </div>;
  }
  // Review date alerts
  const today = new Date().toISOString().split('T')[0];
  const reviewAlerts = patients.flatMap(p => p.encounters.flatMap(enc => enc.followUpDates.filter(d => d <= today).map(d => ({
    patientName: p.personalDetails.fullNames,
    fileNo: p.personalDetails.fileNo,
    date: d,
    patientId: p.id
  }))));
  return <div className="space-y-6" data-id="element-1059">
      {/* Review Date Alerts */}
      {reviewAlerts.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-md p-4" data-id="element-1060">
          <h3 className="text-sm font-bold text-amber-800 mb-2" data-id="element-1061">
            ⚠ Review Visit Reminders
          </h3>
          <ul className="space-y-1" data-id="element-1062">
            {reviewAlerts.map((alert, idx) => <li key={idx} className="text-sm text-amber-700" data-id="element-1063">
                <span className="font-medium" data-id="element-1064">{alert.patientName}</span> (
                {alert.fileNo}) — review was scheduled for{' '}
                <span className="font-medium" data-id="element-1065">
                  {new Date(alert.date).toLocaleDateString()}
                </span>
                {alert.date < today && <span className="text-red-600 font-bold ml-1" data-id="element-1066">(OVERDUE)</span>}
              </li>)}
          </ul>
        </div>}

      <div className="flex justify-between items-center" data-id="element-1067">
        <div className="relative w-96" data-id="element-1068">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1069">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1070" />
          </div>
          <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-teal-500 focus:border-teal-500 sm:text-sm" placeholder="Search patients by name or file no..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1071" />
        </div>
        <button onClick={() => handleOpenForm()} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700" data-id="element-1072">
          <UserPlusIcon className="-ml-1 mr-2 h-5 w-5" data-id="element-1073" />
          Register New Patient
        </button>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md border border-gray-100" data-id="element-1074">
        <ul className="divide-y divide-gray-200" data-id="element-1075">
          {filteredPatients.map(patient => <li key={patient.id} data-id="element-1076">
              <div className="px-4 py-4 sm:px-6 hover:bg-gray-50 flex items-center justify-between" data-id="element-1077">
                <div className="flex items-center" data-id="element-1078">
                  <div className="flex-shrink-0" data-id="element-1079">
                    <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-800 font-bold" data-id="element-1080">
                      {patient.personalDetails.fullNames.charAt(0)}
                    </div>
                  </div>
                  <div className="ml-4" data-id="element-1081">
                    <p className="text-sm font-medium text-teal-600 truncate" data-id="element-1082">
                      {patient.personalDetails.fullNames}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-1083">
                      File No: {patient.personalDetails.fileNo} |{' '}
                      {patient.personalDetails.sex},{' '}
                      {patient.personalDetails.age} yrs
                    </p>
                  </div>
                </div>
                <div data-id="element-1084">
                  <button onClick={() => handleOpenForm(patient)} className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none" data-id="element-1085">
                    <FileTextIcon className="-ml-0.5 mr-2 h-4 w-4 text-gray-400" data-id="element-1086" />
                    Open File
                  </button>
                </div>
              </div>
            </li>)}
          {filteredPatients.length === 0 && <li className="px-4 py-8 text-center text-gray-500" data-id="element-1087">
              No patients found.
            </li>}
        </ul>
      </div>
    </div>;
};